import CarouselHero from "@bmi-digital/components/carousel-hero";
import { useIsClient } from "@bmi-digital/components/hooks";
import Search from "@bmi-digital/components/search";
import { microCopy } from "@bmi/microcopies";
import { graphql } from "gatsby";
import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import Brands from "../components/Brands";
import OverlapCards from "../components/OverlapCards";
import Page from "../components/Page";
import Sections from "../components/Sections";
import WelcomeDialog from "../components/WelcomeDialog";
import { useConfig } from "../contexts/ConfigProvider";
import { getPathWithCountryCode } from "../utils/path";
import { SiteData, useSiteContext } from "../components/Site";
import { getHeroItemsWithContext } from "./helpers/getHeroItemsWithContext";
import type { Data as WelcomeDialogData } from "../components/WelcomeDialog";
import type { PageData as PageData } from "../components/Page";
import type { OverlapCard } from "../components/OverlapCards";
import type { BrandsData as BrandData } from "../components/Brands";
import type { SEOContentData } from "../components/SEOContent";
import type { PromoHero } from "../components/Promo";
import type { PageInfoHero } from "../components/PageInfo";
import type { PayloadHomePage } from "../types/payload";
import type {
  SignupBlockData,
  SignupBlockSectionData
} from "../components/SignupBlock";
import type { MicroCopy as MicroCopyData } from "../components/MicroCopy";
import type { CalculatorConfig } from "../components/pitched-roof-calculator/types";
import type { HouseType } from "../components/visualiser/Types";
import type { GeneralErrorData } from "./general-error";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { Visualiser } from "../components/Visualiser";
import type { CardCollectionSectionData } from "../components/CardCollectionSection";
import type { CarouselSectionData } from "../components/carousel-section/CarouselSection";
import type { SyndicateSectionData } from "../components/SyndicateSection";
import type { PromoSectionData } from "../components/PromoSection";
import type { TitleWithContentSectionData } from "../components/TitleWithContentSection";
import type { ServiceLocatorSectionData } from "../components/service-locator-section";
import type { IframeSectionData } from "../components/IframeSection";
import type { EmbeddedScriptSectionData } from "../components/EmbeddedScriptSection";
import type { VideoSectionData } from "../components/VideoSection";
import type { LeadBlockSectionData } from "../components/LeadBlockSection";

type SlideRelationship = { value: PromoHero | PageInfoHero };
type OverlapCardRelationship = { value: OverlapCard };
export type DefinedOverlapCards = [OverlapCard, OverlapCard, ...OverlapCard[]];

export type HomepageData = Pick<PayloadHomePage, "slug" | "title"> & {
  slides: readonly [SlideRelationship, ...SlideRelationship[]];
  overlapCards: readonly OverlapCardRelationship[] | null;
  brands: readonly BrandData[];
  sections:
    | readonly (
        | CardCollectionSectionData
        | CarouselSectionData
        | EmbeddedScriptSectionData
        | IframeSectionData
        | LeadBlockSectionData
        | PromoSectionData
        | ServiceLocatorSectionData
        | SignupBlockSectionData
        | SyndicateSectionData
        | TitleWithContentSectionData
        | VideoSectionData
      )[]
    | null;
  signupBlock: readonly [SignupBlockData];
  meta: SEOContentData;
};

export type Props = {
  data: {
    payloadHomePage: HomepageData;
    payloadWelcomeDialog: WelcomeDialogData | null;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
  pageContext: {
    variantCodeToPathMap?: Record<string, string>;
  };
};

const HomePageContent = ({
  homePageData,
  welcomeDialog
}: {
  homePageData: HomepageData;
  welcomeDialog: WelcomeDialogData | null;
}) => {
  const siteContext = useSiteContext();
  const theme = useTheme();
  const { isGatsbyDisabledElasticSearch } = useConfig();
  const { isClient } = useIsClient();
  const { countryCode, getMicroCopy } = siteContext;

  const heroItems = getHeroItemsWithContext(
    siteContext,
    theme,
    homePageData.slides
  );

  const overlapCards =
    homePageData.overlapCards && homePageData.overlapCards.length >= 2
      ? (homePageData.overlapCards.map(
          (overlapCard) => overlapCard.value
        ) as DefinedOverlapCards)
      : undefined;

  return (
    <>
      <CarouselHero heroes={heroItems} hasSpaceBottom disableLazyLoading={true}>
        {!isGatsbyDisabledElasticSearch && (
          <Search
            gtm={{
              id: "search2",
              label: getMicroCopy(microCopy.SEARCH_LABEL)
            }}
            action={getPathWithCountryCode(countryCode, "search")}
            label={getMicroCopy(microCopy.SEARCH_LABEL)}
            placeholder={getMicroCopy(microCopy.SEARCH_PLACEHOLDER_HERO)}
          />
        )}
      </CarouselHero>
      {overlapCards && <OverlapCards data={overlapCards} />}
      {homePageData.brands.length > 0 && <Brands data={homePageData.brands} />}
      {homePageData.sections && (
        <Sections
          data-testid="homepage-sections"
          data={homePageData.sections}
          pageTypename={"home-page"}
        />
      )}
      {isClient && welcomeDialog && (
        <WelcomeDialog
          data={{
            welcomeDialogTitle: welcomeDialog.welcomeDialogTitle,
            welcomeDialogBody: welcomeDialog.welcomeDialogBody,
            welcomeDialogBrands: welcomeDialog.welcomeDialogBrands
          }}
        />
      )}
    </>
  );
};

const HomePage = ({ data, pageContext }: Props) => {
  const { title, slides, signupBlock, meta } = data.payloadHomePage;

  const pageData: PageData = useMemo(
    () => ({
      breadcrumbs: null,
      signupBlock,
      meta,
      path: "/"
    }),
    [meta, signupBlock]
  );

  return (
    <Page
      title={title}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
      ogImageUrl={
        "featuredMedia" in slides[0].value && slides[0].value.featuredMedia
          ? slides[0].value.featuredMedia.sizes.heroDesktop.url
          : undefined
      }
      pageType="homePage"
    >
      <HomePageContent
        homePageData={data.payloadHomePage}
        welcomeDialog={data.payloadWelcomeDialog}
      />
    </Page>
  );
};

export default HomePage;

export const pageQuery = graphql`
  query HomePageById {
    payloadHomePage {
      # Only fetching slug to get it in the JSON file so it's indexed
      slug
      title
      slides {
        ... on PayloadPromoOrPage {
          value {
            ...PromoHeroFragment
            ...PageInfoHeroFragment
          }
        }
      }
      overlapCards {
        value {
          ...OverlapCardFragment
        }
      }
      brands {
        ...BrandFragment
      }
      sections {
        ... on PayloadCardCollectionSection {
          ...CardCollectionSectionFragment
        }
        ... on PayloadCarouselSection {
          ...CarouselSectionFragment
        }
        ... on PayloadEmbeddedScriptSection {
          ...EmbeddedScriptSectionFragment
        }
        ... on PayloadIframe {
          ...IframeSectionFragment
        }
        ... on PayloadLeadBlockSection {
          ...LeadBlockSectionFragment
        }
        ... on PayloadPromoSection {
          ...PromoSectionFragment
        }
        ... on PayloadServiceLocatorSection {
          ...ServiceLocatorSectionFragment
        }
        ... on PayloadSignupBlock {
          ...SignupBlockSectionFragment
        }
        ... on PayloadSyndicateSection {
          ...SyndicateSectionFragment
        }
        ... on PayloadTitleWithContent {
          ...TitleWithContentSectionFragment
        }
        ... on PayloadVideoSection {
          ...VideoSectionFragment
        }
      }
      signupBlock {
        ...SignupBlockFragment
      }
      meta {
        ...SEOContentFragment
      }
    }
    payloadWelcomeDialog {
      welcomeDialogTitle
      welcomeDialogBrands
      welcomeDialogBody {
        ...RichTextFragment
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
